import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, PaperPlaneTilt, LinkBreak } from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import { OnboardingForm, Missing, useAnswerSetter } from "@/components/onboarding/OnboardingForm";

const err = (e, fallback) => e.response?.data?.detail || fallback;

const Done = ({ title, children, testId, Icon = CheckCircle }) => (
  <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-6" data-testid={testId}>
    <div className="border-2 border-black bg-white p-8 max-w-lg w-full ob-rise">
      <Icon size={40} weight="bold" className="mb-3" />
      <h1 className="font-chivo font-black uppercase tracking-tighter text-3xl sm:text-4xl leading-none mb-3">{title}</h1>
      <p className="text-sm text-zinc-700">{children}</p>
    </div>
  </div>
);

function useAutosave(token, a, dirty, setDirty, setData) {
  const timer = useRef(null);
  const [saving, setSaving] = useState(false);
  const save = useCallback(async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/public/onboarding/${token}`, { answers: a });
      setData((d) => ({ ...d, missing: data.missing }));
      setDirty(false);
      return true;
    } catch (e) { toast.error(err(e, "Couldn't save. Check your connection and try again.")); return false; }
    finally { setSaving(false); }
  }, [token, a, setDirty, setData]);
  useEffect(() => {
    if (!dirty) { return undefined; }
    clearTimeout(timer.current);
    timer.current = setTimeout(save, 1500);
    return () => clearTimeout(timer.current);
  }, [dirty, save]);
  return { save, saving };
}

export default function BuyerOnboarding() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [a, setA] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoV, setLogoV] = useState(Date.now());
  const set = useAnswerSetter(setA, () => setDirty(true));
  const { save, saving } = useAutosave(token, a, dirty, setDirty, setData);

  useEffect(() => {
    api.get(`/public/onboarding/${token}`)
      .then((r) => { setData(r.data); setA(r.data.answers || null); })
      .catch((e) => setData({ status: "gone", message: err(e, "This setup link isn't valid any more.") }));
  }, [token]);

  const uploadLogo = async (file) => {
    setLogoBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: d } = await api.post(`/public/onboarding/${token}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setData((x) => ({ ...x, has_logo: d.has_logo }));
      setLogoV(Date.now());
      toast.success("Logo uploaded");
    } catch (e) { toast.error(err(e, "Couldn't upload the logo")); }
    finally { setLogoBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/public/onboarding/${token}/submit`, { answers: a });
      setDirty(false);
      setData(d);
      window.scrollTo({ top: 0 });
    } catch (e) { toast.error(err(e, "Couldn't send the form"), { duration: 9000 }); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center label-mono" data-testid="ob-loading">Loading…</div>;
  if (data.status === "gone") return <Done title="Link not found" testId="ob-gone" Icon={LinkBreak}>{data.message}</Done>;
  if (data.status === "live") return <Done title="You're all set" testId="ob-live">{data.firm_name || "Your firm"} is set up and ready to get leads.</Done>;
  if (data.status === "submitted") {
    return <Done title="Thanks — form sent" testId="ob-submitted">We&apos;re checking your answers now. You&apos;ll start getting leads as soon as your setup is approved.</Done>;
  }
  const logo = { src: data.has_logo ? `${API}/public/onboarding/${token}/logo?v=${logoV}` : null, busy: logoBusy, onFile: uploadLogo };
  return (
    <div className="min-h-screen bg-zinc-100" data-testid="buyer-onboarding-page">
      <OnboardingHeader saving={saving} dirty={dirty} onSave={save} />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-6">
        {data.back_note && (
          <div className="border-2 border-black bg-amber-100 p-4 text-sm" data-testid="ob-back-note">
            <div className="label-mono mb-1">A note from our team</div>{data.back_note}
          </div>
        )}
        <OnboardingForm a={a} set={set} price={data.price_per_lead} terms={data.terms} logo={logo} />
        <Missing items={data.missing} />
        <button type="button" onClick={submit} disabled={busy} data-testid="ob-submit"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-black text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-zinc-800 transition-colors duration-100 disabled:opacity-50">
          <PaperPlaneTilt size={16} weight="bold" /> {busy ? "Sending…" : "Send my setup form"}
        </button>
      </main>
    </div>
  );
}

const OnboardingHeader = ({ saving, dirty, onSave }) => (
  <header className="bg-black text-white">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 mb-3">Buyer setup</div>
      <h1 className="font-chivo font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none mb-4">Get ready for leads</h1>
      <p className="text-sm text-zinc-300 max-w-2xl mb-5">
        Answer these questions once and we&apos;ll set everything up: your own branded pages, your thank-you page and how leads reach you.
        It takes about 10 minutes. Your answers save as you go, so you can come back to this link later.
      </p>
      <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest" data-testid="ob-save-state">
        <span className={dirty || saving ? "text-amber-300" : "text-emerald-300"}>{saving ? "Saving…" : dirty ? "Not saved yet" : "All changes saved"}</span>
        {dirty && !saving && <button type="button" onClick={onSave} className="underline" data-testid="ob-save-now">Save now</button>}
      </div>
    </div>
  </header>
);
